import { supabase, sessionManager } from './supabase';
import { Session } from '@supabase/supabase-js';

// Token validation and refresh service
export class TokenService {
  private static instance: TokenService;
  private refreshPromise: Promise<boolean> | null = null;
  private validationCache = new Map<string, { isValid: boolean; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly REFRESH_THRESHOLD = 10 * 60; // 10 minutes before expiry

  private constructor() {}

  public static getInstance(): TokenService {
    if (!TokenService.instance) {
      TokenService.instance = new TokenService();
    }
    return TokenService.instance;
  }

  /**
   * Validates the current JWT token
   */
  public async validateToken(): Promise<{ isValid: boolean; session: Session | null; error?: string }> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('TokenService: Error getting session:', error);
        return { isValid: false, session: null, error: error.message };
      }

      if (!session) {
        return { isValid: false, session: null, error: 'No active session' };
      }

      // Check if token is expired or about to expire
      const isValid = await this.isTokenValid(session);
      
      if (!isValid) {
        // Attempt to refresh the token
        const refreshed = await this.refreshTokenIfNeeded(session);
        if (refreshed) {
          // Get the new session after refresh
          const { data: { session: newSession } } = await supabase.auth.getSession();
          return { isValid: true, session: newSession };
        } else {
          return { isValid: false, session: null, error: 'Token refresh failed' };
        }
      }

      return { isValid: true, session };
    } catch (error) {
      console.error('TokenService: Validation error:', error);
      return { isValid: false, session: null, error: 'Token validation failed' };
    }
  }

  /**
   * Checks if the token is valid and not expired
   */
  private async isTokenValid(session: Session): Promise<boolean> {
    if (!session.expires_at) {
      return false;
    }

    const cacheKey = `${session.access_token.substring(0, 20)}_${session.expires_at}`;
    const cached = this.validationCache.get(cacheKey);
    
    // Return cached result if still valid
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      return cached.isValid;
    }

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at;
    const timeUntilExpiry = expiresAt - now;

    // Consider token invalid if it expires within the refresh threshold
    const isValid = timeUntilExpiry > this.REFRESH_THRESHOLD;
    
    // Cache the result
    this.validationCache.set(cacheKey, {
      isValid,
      timestamp: Date.now()
    });

    // Clean up old cache entries
    this.cleanupCache();

    return isValid;
  }

  /**
   * Refreshes the token if needed
   */
  private async refreshTokenIfNeeded(session: Session): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return await this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh(session);
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    
    return result;
  }

  /**
   * Performs the actual token refresh
   */
  private async performTokenRefresh(session: Session): Promise<boolean> {
    try {
      console.log('TokenService: Refreshing token...');
      
      const { data, error } = await supabase.auth.refreshSession({
        refresh_token: session.refresh_token
      });

      if (error) {
        console.error('TokenService: Refresh error:', error);
        return false;
      }

      if (!data.session) {
        console.error('TokenService: No session returned after refresh');
        return false;
      }

      console.log('TokenService: Token refreshed successfully');
      
      // Clear validation cache after successful refresh
      this.validationCache.clear();
      
      return true;
    } catch (error) {
      console.error('TokenService: Refresh failed:', error);
      return false;
    }
  }

  /**
   * Forces a token refresh
   */
  public async forceRefresh(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return false;
      }

      return await this.performTokenRefresh(session);
    } catch (error) {
      console.error('TokenService: Force refresh failed:', error);
      return false;
    }
  }

  /**
   * Validates token and automatically refreshes if needed
   */
  public async ensureValidToken(): Promise<Session | null> {
    const validation = await this.validateToken();
    
    if (validation.isValid && validation.session) {
      return validation.session;
    }

    // If validation failed, try to get a fresh session
    const session = await sessionManager.getValidSession();
    return session;
  }

  /**
   * Checks if the user is authenticated with a valid token
   */
  public async isAuthenticated(): Promise<boolean> {
    const validation = await this.validateToken();
    return validation.isValid;
  }

  /**
   * Gets the current user ID from the token
   */
  public async getCurrentUserId(): Promise<string | null> {
    try {
      const session = await this.ensureValidToken();
      return session?.user?.id || null;
    } catch (error) {
      console.error('TokenService: Error getting user ID:', error);
      return null;
    }
  }

  /**
   * Gets token expiration time
   */
  public async getTokenExpiration(): Promise<Date | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.expires_at) {
        return null;
      }
      return new Date(session.expires_at * 1000);
    } catch (error) {
      console.error('TokenService: Error getting token expiration:', error);
      return null;
    }
  }

  /**
   * Cleans up old cache entries
   */
  private cleanupCache(): void {
    const now = Date.now();
    for (const [key, value] of this.validationCache.entries()) {
      if (now - value.timestamp > this.CACHE_DURATION) {
        this.validationCache.delete(key);
      }
    }
  }

  /**
   * Clears all cached validation results
   */
  public clearCache(): void {
    this.validationCache.clear();
  }

  /**
   * Sets up automatic token refresh
   */
  public setupAutoRefresh(): void {
    // Check token validity every 5 minutes
    setInterval(async () => {
      try {
        const isAuth = await this.isAuthenticated();
        if (!isAuth) {
          console.log('TokenService: Auto-refresh detected invalid token');
          // The validation process will handle refresh automatically
        }
      } catch (error) {
        console.error('TokenService: Auto-refresh check failed:', error);
      }
    }, 5 * 60 * 1000); // 5 minutes
  }
}

// Export singleton instance
export const tokenService = TokenService.getInstance();

// Initialize auto-refresh on module load
tokenService.setupAutoRefresh();